/**
 * Signature Cache Utility
 *
 * This module manages signature caching to prevent requiring multiple signatures for the same message.
 * It stores signatures in localStorage and provides methods to check and update the cache.
 */

// Prefix for localStorage keys to avoid conflicts
const STORAGE_PREFIX = 'bitp_sig_cache_';

// Default expiration time for signatures (14 days in milliseconds)
const DEFAULT_EXPIRY = 14 * 24 * 60 * 60 * 1000;

/**
 * Calculate a hash for a message to use as a cache key
 * This is a simple implementation and could be improved with a proper hash function
 * @param {string} message - The message being signed
 * @returns {string} A hash string to use as a cache key
 */
const getMessageHash = (message) => {
  // Simple hash function for demo purposes
  // In production, use a proper hash function
  return btoa(message).replace(/[^a-zA-Z0-9]/g, '');
};

/**
 * Check if a signature for the message already exists in cache
 * @param {string} message - The message to check
 * @param {string} account - The wallet address (to scope signatures per account)
 * @returns {string|null} The cached signature or null if not found
 */
export const getCachedSignature = (message, account) => {
  if (!message || !account) return null;

  try {
    const key = `${STORAGE_PREFIX}${account}_${getMessageHash(message)}`;
    const cached = localStorage.getItem(key);

    if (cached) {
      // Check if the signature has expired
      const { signature, timestamp } = JSON.parse(cached);
      const now = Date.now();

      if (now - timestamp < DEFAULT_EXPIRY) {
        console.log('Using cached signature for message:', message.substring(0, 30) + '...');
        return signature;
      } else {
        // Signature expired, remove it
        console.log('Signature expired, removing from cache');
        localStorage.removeItem(key);
        return null;
      }
    }

    return null;
  } catch (error) {
    console.error('Error retrieving cached signature:', error);
    return null;
  }
};

/**
 * Cache a signature for future use
 * @param {string} message - The message that was signed
 * @param {string} signature - The signature to cache
 * @param {string} account - The wallet address (to scope signatures per account)
 */
export const cacheSignature = (message, signature, account) => {
  if (!message || !signature || !account) return;

  try {
    const key = `${STORAGE_PREFIX}${account}_${getMessageHash(message)}`;
    const value = JSON.stringify({
      signature,
      timestamp: Date.now(),
      message: message.substring(0, 100) // Store part of the message for debugging
    });

    localStorage.setItem(key, value);
    console.log('Cached signature for message:', message.substring(0, 30) + '...');

    // Also cache a special "last signature" entry for this account
    // This can be used to quickly check if we have any valid signatures
    const lastSignatureKey = `${STORAGE_PREFIX}${account}_last_signature`;
    localStorage.setItem(lastSignatureKey, Date.now().toString());
  } catch (error) {
    console.error('Error caching signature:', error);
  }
};

/**
 * Clear all cached signatures for an account
 * @param {string} account - The wallet address
 */
export const clearCachedSignatures = (account) => {
  if (!account) return;

  try {
    // Find all keys that match the prefix + account
    const keyPrefix = `${STORAGE_PREFIX}${account}_`;

    // Get all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(keyPrefix)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error('Error clearing cached signatures:', error);
  }
};

/**
 * Wraps an Ethereum signing method to use the signature cache
 * @param {Function} signingMethod - The original signing method to wrap
 * @param {Object} options - Options for the wrapper
 * @param {boolean} options.forceRefresh - If true, ignores cache and generates a new signature
 * @returns {Function} A wrapped function that checks cache before signing
 */
export const withSignatureCache = (signingMethod, { forceRefresh = false } = {}) => {
  return async (message, account) => {
    // Skip cache if forceRefresh is true
    if (!forceRefresh) {
      const cachedSignature = getCachedSignature(message, account);
      if (cachedSignature) {
        console.log('Using cached signature from wrapper');
        return cachedSignature;
      }
    }

    // No cached signature found or force refresh, get a new one
    console.log('No cached signature found, requesting new signature');
    const signature = await signingMethod(message);

    // Cache the new signature
    cacheSignature(message, signature, account);

    return signature;
  };
};

/**
 * Check if there's any valid cached signature for the account
 * This is useful to quickly determine if we should try to use cached signatures
 * @param {string} account - The wallet address
 * @returns {boolean} True if there's at least one valid cached signature
 */
export const hasValidCachedSignatures = (account) => {
  if (!account) return false;

  try {
    const lastSignatureKey = `${STORAGE_PREFIX}${account}_last_signature`;
    const lastSignatureTime = localStorage.getItem(lastSignatureKey);

    if (lastSignatureTime) {
      const now = Date.now();
      return (now - parseInt(lastSignatureTime, 10)) < DEFAULT_EXPIRY;
    }

    return false;
  } catch (error) {
    console.error('Error checking for valid cached signatures:', error);
    return false;
  }
};

export default {
  getCachedSignature,
  cacheSignature,
  clearCachedSignatures,
  withSignatureCache,
  hasValidCachedSignatures
};
